import { QualificationQuestionsAnswers } from 'modules/data/domain';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { clearLocalStorageValue, getLocalStorageParsedValue, setLocalStorageValue } from 'shared/helpers';

import { answersDataStorageKey, formDataStorageKey } from './ProfileSetupForm.constants';
import { ProfileFormData } from './ProfileSetupForm.types';

export function useFormSyncState(
    profileData: ProfileFormData,
    onDataChange: (data: ProfileFormData) => void,
    showPhoneField: boolean | undefined,
    saveStateInLocalStorage?: boolean
) {
    const changeData = useCallback(
        (diff: Partial<ProfileFormData>) => {
            const data: ProfileFormData = {
                ...profileData,
                ...diff,
            };

            onDataChange(data);

            if (saveStateInLocalStorage) {
                setLocalStorageValue(formDataStorageKey, {
                    firstName: data.firstName,
                    lastName: data.lastName,
                    phone: data.phone,
                });
            }
        },
        [profileData, onDataChange, saveStateInLocalStorage]
    );

    const changeFirstName = useCallback(
        (firstName: string) => {
            changeData({
                firstName,
            });
        },
        [changeData]
    );

    const changeLastName = useCallback(
        (lastName: string) => {
            changeData({
                lastName,
            });
        },
        [changeData]
    );

    const changePhone = useCallback(
        (phone: ProfileFormData['phone']) => {
            changeData({
                phone,
            });
        },
        [changeData]
    );

    const changePassword = useCallback(
        (password: string) => {
            changeData({
                password,
            });
        },
        [changeData]
    );

    const [isPhoneTouched, setIsPhoneTouched] = useState(false);

    useEffect(() => {
        if (!saveStateInLocalStorage) {
            return;
        }

        const savedFormState = getLocalStorageParsedValue<Partial<ProfileFormData>>(formDataStorageKey);

        const restoredData: Partial<ProfileFormData> = {};

        if (!profileData.firstName && savedFormState?.firstName) {
            restoredData.firstName = savedFormState.firstName;
        }

        if (!profileData.lastName && savedFormState?.lastName) {
            restoredData.lastName = savedFormState.lastName;
        }

        if (showPhoneField && !profileData.phone && savedFormState?.phone) {
            restoredData.phone = savedFormState.phone;
            setIsPhoneTouched(true);
        }

        if (Object.keys(restoredData).length > 0) {
            changeData(restoredData);
        }
        // on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        changeFirstName,
        changeLastName,
        changePhone,
        changePassword,
        isPhoneTouched,
        setIsPhoneTouched,
    };
}

export function useAnswersSyncState(showQuestions?: boolean, saveStateInLocalStorage?: boolean) {
    const qualificationAnswersInitial = useMemo(() => {
        if (saveStateInLocalStorage && showQuestions) {
            const data = getLocalStorageParsedValue<QualificationQuestionsAnswers>(answersDataStorageKey);

            if (data) {
                return data;
            }
        }

        return null;
        // on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [qualificationQuestionsAnswers, setQualificationQuestionsAnswers] =
        useState<QualificationQuestionsAnswers | null>(qualificationAnswersInitial);

    const handleChangeQualificationQuestionsAnswers = useCallback(
        (data: QualificationQuestionsAnswers | null) => {
            setQualificationQuestionsAnswers(data);

            if (!saveStateInLocalStorage) {
                return;
            }

            if (data) {
                setLocalStorageValue(answersDataStorageKey, data);
            } else {
                clearLocalStorageValue(answersDataStorageKey);
            }
        },
        [saveStateInLocalStorage]
    );

    return {
        qualificationAnswersInitial,
        qualificationQuestionsAnswers,
        handleChangeQualificationQuestionsAnswers,
    };
}
